import { useState, useCallback } from "react";
import axios from "axios";
import { Moment } from "moment";
import cSharpApi from "../../../../../../services/api/CSharpApi";

export type ColumnLineMultiHistoricalData = {
  time: Moment;
  column: number;
  line: number;
  columnDisplayUnit: string;
  lineDisplayUnit: string;
};

export type ColumnLineMultiChartData = {
  historicalData: ColumnLineMultiHistoricalData[];
  hasData: boolean;
  errorMessage: string;
};

type ColumnLineMultiChartState = {
  data: ColumnLineMultiChartData;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  message?: string;
};

type FetchParams = {
  duration: number;
  resolution: number;
  customEndDate?: number;
  hoursToAdd: number;

  columnName: string;
  columnFieldKey: string;
  lineName: string;
  lineFieldKey: string;

  columnDisplayName?: string;
  columnFieldName?: string;
  columnPortNumber: number;
  lineDisplayName?: string;
  lineFieldName?: string;
  linePortNumber: number;
  orgId?: string;
};

const INITIAL_STATE = {
  data: {
    historicalData: [],
    hasData: false,
    errorMessage: "",
  },
  isLoading: false,
  hasError: false,
  errorMessage: "",
  message: "",
};

const useHistoricalColumnLineMulti = (deviceId: string) => {
  const [fetchedData, setFetchedData] = useState<ColumnLineMultiChartState>(INITIAL_STATE);

  const cancelTokenSource = axios.CancelToken.source();

  const fetchData = useCallback(
    async (params: FetchParams) => {
      setFetchedData({
        ...INITIAL_STATE,
        isLoading: true,
      });

      try {
        const response = await cSharpApi.post(
          `charts/get-historical-column-and-line-multi/${deviceId}`,
          JSON.stringify(params),
          {
            cancelToken: cancelTokenSource.token,
          },
        );

        const data = await response.data.data;

        if (data) {
          setFetchedData({
            data,
            isLoading: false,
            hasError: false,
            errorMessage: "",
            message: response.data.message,
          });
        }
      } catch (error) {
        let errorMessage = "fetching data aborted";

        if (axios.isCancel(error)) {
          console.log("fetching data aborted");
        } else {
          errorMessage = error instanceof Error ? error.message : "Unknown error";
          console.log("An error occurred", error);
        }

        setFetchedData({
          ...INITIAL_STATE,
          errorMessage,
        });
      }
      return () => cancelTokenSource.cancel();
    },
    [deviceId],
  );

  const { data, isLoading, hasError, errorMessage, message } = fetchedData;

  return { fetchData, data, isLoading, hasError, errorMessage, message };
};

export default useHistoricalColumnLineMulti;
